import { create } from 'zustand'
import { parseSignOnSavePrefs, type SignOnSaveKey, type SignOnSavePreferences } from '@/shared/lib/signOnSavePrefs'
import { signOnSavePrefsRepository } from '@/shared/repositories/settings/signOnSavePrefsRepository'

const initialData = () => ({
  ownerUserId: null as string | null,
  preferences: parseSignOnSavePrefs(null),
  confirmed: parseSignOnSavePrefs(null),
  loaded: false,
  loading: false,
  saving: 0,
})

interface SignOnSavePrefsState extends ReturnType<typeof initialData> {
  load: (userId: string) => Promise<void>
  setPreference: (userId: string, key: SignOnSaveKey, enabled: boolean) => Promise<void>
  reset: () => void
}

let generation = 0
let activeLoad: { userId: string; promise: Promise<void> } | null = null
const revisions: Partial<Record<SignOnSaveKey, number>> = {}

/** Estado compartilhado entre Configurações e o editor; nunca persiste escolhas de outro médico. */
export const useSignOnSavePrefsStore = create<SignOnSavePrefsState>((set, get) => ({
  ...initialData(),
  load: async (userId) => {
    if (get().ownerUserId === userId && get().loaded) return
    if (activeLoad?.userId === userId) return activeLoad.promise
    const ticket = ++generation
    set({ ...initialData(), ownerUserId: userId, loading: true })
    const promise = signOnSavePrefsRepository.getSignOnSavePreferences(userId)
      .then((preferences) => {
        if (ticket !== generation) return
        set({ preferences, confirmed: preferences, loaded: true, loading: false })
      })
      .catch((error: unknown) => {
        if (ticket === generation) set({ loading: false })
        throw error
      })
      .finally(() => {
        if (activeLoad?.promise === promise) activeLoad = null
      })
    activeLoad = { userId, promise }
    return promise
  },

  setPreference: async (userId, key, enabled) => {
    await get().load(userId)
    if (get().ownerUserId !== userId || !get().loaded) throw new Error('Sessão alterada. Tente novamente.')
    const ticket = generation
    const revision = (revisions[key] ?? 0) + 1
    revisions[key] = revision
    set((state) => ({ preferences: { ...state.preferences, [key]: enabled }, saving: state.saving + 1 }))
    try {
      await signOnSavePrefsRepository.updateSignOnSavePreferences({ [key]: enabled }, userId)
      if (ticket === generation) set((state) => ({ confirmed: { ...state.confirmed, [key]: enabled } }))
    } catch (error) {
      if (ticket === generation && revisions[key] === revision) {
        set((state) => ({ preferences: { ...state.preferences, [key]: state.confirmed[key] } }))
      }
      throw error
    } finally {
      if (ticket === generation) set((state) => ({ saving: Math.max(0, state.saving - 1) }))
    }
  },

  reset: () => {
    generation += 1
    activeLoad = null
    set(initialData())
  },
}))

export type { SignOnSavePreferences }
